/**
 * Client-side cleanup after auth tokens are cleared (logout / session exit).
 *
 * Clears in-memory Pinia data caches, ``sessionStorage``, and origin Cache Storage.
 * Keeps ``localStorage`` locale/theme keys — only auth keys should be removed in ``auth`` store.
 */
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/auth.service'
import { useUserStore } from '@/stores/user'
import { useCustomerStore } from '@/stores/customers'
import { useQuotationsStore } from '@/stores/quotations'
import { useProjectStore } from '@/stores/projects'
import { useContractsStore } from '@/stores/contracts'
import { useSellingStore } from '@/stores/selling'
import { usePointsStore } from '@/stores/points'

function resetListStores(): void {
  useCustomerStore().$reset()
  useQuotationsStore().$reset()
  useProjectStore().$reset()
  useContractsStore().$reset()
  useSellingStore().$reset()
  usePointsStore().$reset()
}

/**
 * Full logout: revoke session on the server (clears HttpOnly cookies), then wipe client state.
 *
 * Auth tokens live in HttpOnly cookies — JavaScript cannot delete them; ``POST /auth/logout`` must run.
 */
export async function performLogout(): Promise<void> {
  try {
    await authService.logout()
  } catch {
    /* session may already be invalid; still clear client state */
  }
  useAuthStore().logout()
  finalizeClientLogoutAfterAuthCleared()
}

/**
 * Run after ``useAuthStore().logout()`` (tokens + branding cleared).
 *
 * Drops user profile/permissions from Pinia, resets module list stores, and clears
 * tab session + service-worker caches so the next login does not reuse stale data.
 */
export function finalizeClientLogoutAfterAuthCleared(): void {
  useUserStore().clearProfile()
  resetListStores()
  try {
    sessionStorage.clear()
  } catch {
    /* ignore (non-browser) */
  }
  if (typeof caches !== 'undefined') {
    void caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
  }
}
